/*
 * Copyright (c) 2022. Muzology
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { RudderstackProvider, useRudderStackAnalytics } from './RudderstackProvider';
import { BUILD_ENVIRONMENT, CODE_BRANCH, CODE_VERSION, GOOGLE_TAG_MANAGER_ID, HEAP_ANALYTICS, LOGROCKET_ID, MUZOLOGY_API, ROLLBAR_TOKEN, WEBSITE_URL } from '@/lib/defaults';
import LogRocket from 'logrocket';
import { Provider as RollbarProvider, useRollbar } from '@rollbar/react';
import posthog from 'posthog-js';

const Context = React.createContext(undefined);

/* eslint-disable camelcase */
const rollbarConfig = {
    accessToken: ROLLBAR_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    payload: {
        environment: BUILD_ENVIRONMENT,
        //trace_id: 'abc',
        client: {
            javascript: {
                code_version: CODE_VERSION,
                source_map_enabled: true,
                guess_uncaught_frames: true
            }
        },
        server: {
            root: '/',
            host: WEBSITE_URL,
            branch: CODE_BRANCH
        }
    }
};
/* eslint-enable camelcase */

export const AnalyticsProvider = ({ children, pageProps }) => {
    const [logRocketSessionURL, setLogRocketSessionURL] = React.useState(null);
    const value = { logRocketSessionURL };

    React.useEffect(() => {
        if (GOOGLE_TAG_MANAGER_ID) {
            console.info('[Analytics] Google Tag Manager ID:', GOOGLE_TAG_MANAGER_ID);
        } else {
            console.warn('[Analytics] No Google Tag Manager ID');
        }

        if (ROLLBAR_TOKEN) {
            console.info('[Analytics] Rollbar Token:', ROLLBAR_TOKEN);
        } else {
            console.warn('[Analytics] No Rollbar Token');
        }

        if (HEAP_ANALYTICS) {
            console.info('[Analytics] Heap Analytics:', HEAP_ANALYTICS);
        } else {
            console.warn('[Analytics] No Heap Analytics');
        }

        if (LOGROCKET_ID) {
            console.info('[Analytics] LogRocket ID', LOGROCKET_ID);
        } else {
            console.warn('No LogRocket ID');
        }
    }, []);

    return (
        <RollbarProvider config={rollbarConfig}>
            <Context.Provider value={value}>
                <RudderstackProvider pageProps={pageProps}>
                    <AnalyticsIdentity setLogRocketSessionURL={setLogRocketSessionURL}>
                        {children}
                    </AnalyticsIdentity>
                    {/**/}
                </RudderstackProvider>
            </Context.Provider>
        </RollbarProvider>
    );
};


const AnalyticsIdentity = ({ children, setLogRocketSessionURL }) => {
    const analytics = useAnalytics();
    const rudderstack = useRudderStackAnalytics();
    const rollbar = useRollbar();

    const getHeap = () => window.heap;

    useEffect(() => {
        const heap = getHeap();
        console.log('[heap] push', heap?.push);
        console.log('[heap] heap', heap);
    }, []);

    useEffect(() => {
        console.log('[Analytics Identity] initializing analytics identity', { rudderstack, analytics });
        if (!rudderstack) {
            console.warn('[Analytics Identity] Rudderstack not available');
            return;
        }

        try {
            const anonymousId = analytics?.getAnonymousId();
            console.log('[Analytics] anonymousId = ', anonymousId);
            if (LOGROCKET_ID) {
                console.log('[Logrocket] init ', LOGROCKET_ID);
                LogRocket.init(LOGROCKET_ID, { rootHostname: 'muzology.com' });
                console.log('[Logrocket] identify ', LOGROCKET_ID);
                LogRocket.identify({ anonymousId });

                // identify in PostHog
                if (anonymousId) {
                    console.log('[PostHog] identify', anonymousId);
                    posthog.identify(anonymousId);
                }

                const heap = getHeap();
                console.log('[heap] mount heap =', heap);
                if (window.heap) {
                    LogRocket.getSessionURL((logRocketSessionUrl: string) => {
                        window.heap.track('LogRocket Session URL', { logRocketSessionUrl });
                    });
                } else {
                    console.warn('[Heap] Heap not available');
                }
            } else {
                console.warn('[Logrocket] LogRocket not available');
            }
        } catch (e) {
            console.error('[Logrocket] error setting up analytics', e);
            rollbar?.error('error setting up analytics', e);
        }
    }, [rudderstack]);

    return children;
};

export const useAnalytics = () => {
    const rollbar = useRollbar();
    const getHeap = () => window.heap;
    const rudderstack = useRudderStackAnalytics();

    return useMemo(() => (
        {
            rudderstack: {},
            anonymousId: rudderstack?.getAnonymousId(),
            track(...args) {
                console.debug('[Analytics] Track:', ...args);

                // Track with Heap
                try {
                    const heap = getHeap();
                    if (heap) {
                        heap.track(...args);
                    }
                } catch (e) {
                    console.error('[Heap] error tracking event', e);
                    rollbar?.error('error tracking event', e);
                }

                // Track with Rudderstack
                if (rudderstack) {
                    rudderstack.track(...args);
                } else {
                    console.warn('[Analytics] Rudderstack not available');
                }

                // Track with PostHog
                try {
                    const [eventName, properties] = args;
                    posthog.capture(eventName, properties || {});
                } catch (e) {
                    console.error('[PostHog] error tracking event', e);
                    rollbar?.error('PostHog error tracking event', e);
                }

                return Promise.resolve();
            },
            page(...args) {
                console.debug('[Analytics] Page:', ...args);
                // Rudderstack page call omitted

                // PostHog pageview
                try {
                    const [pageName, props] = args;
                    posthog.capture('$pageview', { name: pageName, ...(props || {}) });
                } catch (e) {
                    console.error('[PostHog] error tracking pageview', e);
                    rollbar?.error('PostHog error tracking pageview', e);
                }
            },
            identify(...args) {
                console.debug('[Analytics] Identify:', ...args);

                // Rudderstack identify
                if (rudderstack)
                    rudderstack.identify(...args);

                // Heap identify
                try {
                    const heap = getHeap();
                    if (heap) {
                        heap.addUserProperties(args as any);
                    }
                } catch (e) {
                    console.error('[Heap] error identifying user', e);
                    rollbar?.error('error identifying user', e);
                }

                // LogRocket identify
                try {
                    if (LOGROCKET_ID) {
                        LogRocket.identify(args as any);
                    }
                } catch (e) {
                    console.error('[LogRocket] error identifying user', e);
                    rollbar?.error('error identifying user', e);
                }

                // PostHog identify
                try {
                    const userId = typeof args[0] === 'string' ? args[0] : args[0]?.userId;
                    const traits = args.length > 1 ? args[1] : undefined;
                    if (userId) {
                        posthog.identify(userId, traits);
                    }
                } catch (e) {
                    console.error('[PostHog] error identifying user', e);
                    rollbar?.error('PostHog error identifying user', e);
                }
            },
            getAnonymousId() {
                const id = rudderstack?.getAnonymousId() ?? null;
                console.log('[Analytics] getAnonymousId()', id);
                return id;
            },
            getLogrocketSessionURL() {
                console.log('[Analytics] getLogrocketSessionURL()');
                return "logrocket url";
            },
            reset() {
                console.log('[Analytics] reset()');
            }
        }
    ), [rollbar, rudderstack]);
};

export const BlockKlaviyo = { integrations: { All: true, Klaviyo: false } };

export default AnalyticsProvider;